/**
 * apiCache — tiny module-level cache for read-only GET endpoints.
 *
 * Two problems it solves:
 *   1. Page nav refetches: revisiting Jobs after Workflow re-fires
 *      /jobs/filter-options and /crews even though they rarely change.
 *   2. Parallel duplicate calls: JobsPage and MediaCenterPage both
 *      fetch /jobs/filter-options on mount; if you nav fast they fire
 *      twice in a second.
 *
 * Usage:
 *   import { cachedGet, invalidate } from '@/lib/apiCache';
 *   const { data } = await cachedGet('/api/jobs/filter-options');
 *   // After a mutation:
 *   invalidate('/api/jobs/filter-options');
 *
 * Semantics:
 *   - Default TTL: 5 minutes. Override per-call with { ttlMs }.
 *   - In-flight dedup: two concurrent callers for the same URL share
 *     ONE network request.
 *   - Cache key = URL only (no headers / params variation handled).
 *     Pass already-built query strings in the URL.
 *   - On error: the failed promise is NOT cached; the next call retries.
 *
 * What NOT to cache:
 *   - POST/PUT/DELETE responses (mutations)
 *   - Endpoints with real-time state (sync-status, generation progress)
 *   - User-specific responses that depend on URL params not in the key
 */
import axios from 'axios';

const DEFAULT_TTL_MS = 5 * 60 * 1000;          // 5 minutes
const store = new Map();                       // url -> { data, expires }
const inflight = new Map();                    // url -> Promise

export async function cachedGet(url, { ttlMs = DEFAULT_TTL_MS, withCredentials = true, headers = undefined } = {}) {
  const now = Date.now();
  const hit = store.get(url);
  if (hit && hit.expires > now) {
    return { data: hit.data, fromCache: true };
  }
  if (inflight.has(url)) {
    return inflight.get(url);
  }
  const axiosCfg = { withCredentials };
  if (headers) axiosCfg.headers = headers;
  const p = axios.get(url, axiosCfg)
    .then(res => {
      store.set(url, { data: res.data, expires: Date.now() + ttlMs });
      inflight.delete(url);
      return { data: res.data, fromCache: false };
    })
    .catch(err => {
      inflight.delete(url);
      throw err;
    });
  inflight.set(url, p);
  return p;
}

export function invalidate(url) {
  store.delete(url);
  inflight.delete(url);
}

/**
 * Invalidate every cached URL whose KEY starts with `prefix` OR
 * whose pathname-portion starts with `prefix`.
 *
 * Why both? Cache keys are typically stored as full URLs
 * (`https://host/api/jobs`). Callers may want to invalidate by
 * either form:
 *   • Full URL prefix: `https://host/api/jobs` (legacy direct callers)
 *   • Path-only prefix: `/api/jobs` (used by the interceptor since
 *     the response.config.url's full host may not be known at
 *     install time, e.g. relative URLs).
 *
 * A POST to `/api/jobs` needs to blow away `https://host/api/jobs`
 * AND `https://host/api/jobs?limit=500` — both stored under
 * different cache keys.
 */
export function invalidatePrefix(prefix) {
  if (!prefix) return 0;
  const matches = (key) => {
    if (key.startsWith(prefix)) return true;
    // Try matching against the URL's path portion. Works for both
    // absolute URLs (https://host/api/jobs?x=1) and relative
    // ones (/api/jobs?x=1).
    try {
      const p = new URL(key, 'http://_').pathname;
      return p.startsWith(prefix);
    } catch {
      return false;
    }
  };
  let n = 0;
  for (const key of Array.from(store.keys())) {
    if (matches(key)) {
      store.delete(key);
      n += 1;
    }
  }
  for (const key of Array.from(inflight.keys())) {
    if (matches(key)) {
      inflight.delete(key);
    }
  }
  return n;
}

export function invalidateAll() {
  store.clear();
  inflight.clear();
}
